import * as React from 'react';
import EqualizerIcon from '@material-ui/icons/Equalizer';
import { ActivityAction, ActivityResourceName } from '@pec/aion-ui-core/interfaces/activities';
import { AnchorListItem } from './AnchorListItem';
import { AppName } from '@pec/aion-ui-core/interfaces/appName';
import { DeepReadonly } from 'utility-types';
import { Feature } from 'flagged';
import { FeatureFlagWrapper } from '@pec/aion-ui-core/features/featureFlags/components/FeatureFlagWrapper';
import { IServerTokens } from '@pec/aion-ui-core/interfaces/serverTokens';
import { NavLinkListItem } from './NavLinkListItem';
import { OrganizationFeature } from '@pec/aion-ui-core/interfaces/organization';
import { SectionContainer } from '../../containers/Nav/Section';
import { useTranslation } from 'react-i18next';

const { AionReportingUI, AionOshaViolationsUI } = AppName;
const { Organizations, OshaViolations } = ActivityResourceName;
const { Read, Manage } = ActivityAction;

const {
  DeficiencyReports,
  FlexTrack,
  IncidentReports,
  MinimumStandardsReports,
  QuestionnaireV2,
  RegionMetrics,
  RegionMetricsWithoutTotal,
  OperationalMetrics
} = OrganizationFeature;

export type Props = {
  features: DeepReadonly<OrganizationFeature[]>;
  hasOrganizationPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  hasGlobalPermission: (action: ActivityAction, resourceName: ActivityResourceName) => boolean;
  organizationId: string;
  serverTokens: IServerTokens;
};

export const ReportsSection: React.FC<Props> = ({
  features,
  organizationId,
  serverTokens: { complianceReportsUrl },
  hasOrganizationPermission,
  hasGlobalPermission
}) => {
  const { t } = useTranslation();
  const shouldShowLegacyReports = !features.includes(QuestionnaireV2);
  const isAdmin = hasGlobalPermission(Manage, Organizations);

  return (
    <SectionContainer
      id={AionReportingUI}
      subheader={t('components.reportsSection.reports', 'Reports')}
      icon={<EqualizerIcon />}
    >
      {complianceReportsUrl && (
        <FeatureFlagWrapper>
          <Feature name="SisenseComplianceReports">
            <AnchorListItem inset href={complianceReportsUrl} target="_blank">
              {t('components.reportsSection.complianceReports', 'Compliance Reports')}
            </AnchorListItem>
          </Feature>
        </FeatureFlagWrapper>
      )}
      {shouldShowLegacyReports && (
        <AnchorListItem inset href="/SSQV4/Reports/BLSReports.aspx">
          {t('components.reportsSection.blsRollup', 'BLS Rollup')}
        </AnchorListItem>
      )}
      {(isAdmin || shouldShowLegacyReports) && (
        <AnchorListItem inset href="/SSQV4/Reports/CustomReportsTree.aspx">
          {t('components.reportsSection.buildReports', 'Build Reports')}
        </AnchorListItem>
      )}
      {shouldShowLegacyReports && (
        <AnchorListItem inset href="/SSQV4/Reports/ContractorRankingReport.aspx?edx=">
          {t('components.reportsSection.contractorStatus', 'Contractor Status')}
        </AnchorListItem>
      )}
      {(shouldShowLegacyReports || isAdmin) && (
        <AnchorListItem inset href="/SSQV4/Reports/SpecificallyConfiguredReportsViewerTree.aspx">
          {t('components.reportsSection.customReports', 'Custom Reports')}
        </AnchorListItem>
      )}
      {shouldShowLegacyReports && features.includes(DeficiencyReports) && (
        <AnchorListItem inset href="/SSQV4/Deficiency/DeficiencyReportsTree.aspx">
          {t('components.reportsSection.deficiencies', 'Deficiencies')}
        </AnchorListItem>
      )}
      {(features.includes(FlexTrack) ||
        features.includes(RegionMetrics) ||
        features.includes(RegionMetricsWithoutTotal)) &&
        (process.env.REACT_APP_NAME === AionReportingUI ? (
          <NavLinkListItem inset to={`/${organizationId}/reporting/regional`}>
            {t('components.common.flexTrack', 'FlexTrack')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/reporting/regional`}>
            {t('components.common.flexTrack', 'FlexTrack')}
          </AnchorListItem>
        ))}
      {features.includes(IncidentReports) &&
        (process.env.REACT_APP_NAME === AionReportingUI ? (
          <NavLinkListItem inset to={`/${organizationId}/reporting/incidents`}>
            {t('components.reportsSection.leadingIndicators', 'Leading Indicators')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/reporting/incidents`}>
            {t('components.reportsSection.leadingIndicators', 'Leading Indicators')}
          </AnchorListItem>
        ))}
      {shouldShowLegacyReports && (
        <>
          <AnchorListItem inset href="/SSQV4/Reports/MonthlyIncidentDataDetailReport.aspx">
            {t('components.reportsSection.manHoursReport', 'Man-Hours Report')}
          </AnchorListItem>
          {features.includes(MinimumStandardsReports) && (
            <React.Fragment>
              <AnchorListItem
                inset
                href="/SSQV4/Requirements/TranslateRequirements.aspx?tp=RequirementReportsFrameset.htm"
              >
                {t('components.reportsSection.minimumStandards', 'Minimum Standards')}
              </AnchorListItem>
              <AnchorListItem inset href="/SSQV4/Requirements/RequirementMigrationListing.aspx">
                {t('components.reportsSection.minimumStandardsChanges', 'Minimum Standards Changes')}
              </AnchorListItem>
            </React.Fragment>
          )}
        </>
      )}
      {(shouldShowLegacyReports || isAdmin) && (
        <AnchorListItem inset href="/SSQV4/Reports/CustomReportsViewerTree.aspx">
          {t('components.reportsSection.myReports', 'My Reports')}
        </AnchorListItem>
      )}
      {features.includes(OperationalMetrics) &&
        (process.env.REACT_APP_NAME === AionReportingUI ? (
          <NavLinkListItem inset to={`/${organizationId}/reporting/operations/periods`}>
            {t('components.reportsSection.operations', 'Operations')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/reporting/operations/periods`}>
            {t('components.reportsSection.operations', 'Operations')}
          </AnchorListItem>
        ))}
      {(hasOrganizationPermission(Read, OshaViolations) || hasGlobalPermission(Read, OshaViolations)) &&
        (process.env.REACT_APP_NAME === AionOshaViolationsUI ? (
          <NavLinkListItem exact inset to={`/organizations/${organizationId}/osha-violations/client/information`}>
            {t('components.reportsSection.oshaViolations', 'OSHA Violations')}
          </NavLinkListItem>
        ) : (
          <AnchorListItem inset href={`/organizations/${organizationId}/osha-violations/client/information`}>
            {t('components.reportsSection.oshaViolations', 'OSHA Violations')}
          </AnchorListItem>
        ))}
      <AnchorListItem inset href="/SSQV4/Reports/UserPasswordReset.aspx">
        {t('components.reportsSection.passwordAuditReport', 'Password Audit Report')}
      </AnchorListItem>
      <AnchorListItem inset href="/SSQV4/ReleaseMaintenance/CurrentReleaseReport.aspx">
        {t('components.reportsSection.releases', 'Releases')}
      </AnchorListItem>
      {shouldShowLegacyReports && (
        <>
          <AnchorListItem inset href="/SSQV4/Scoring/ScoringReportsTree.aspx">
            {t('components.reportsSection.scoring', 'Scoring')}
          </AnchorListItem>
          <AnchorListItem inset href="/SSQV4/Scoring/MigrationReportsTree.aspx">
            {t('components.reportsSection.scoringChanges', 'Scoring Changes')}
          </AnchorListItem>
        </>
      )}
      {(shouldShowLegacyReports || isAdmin) && (
        <AnchorListItem inset href="/SSQV4/Reports/StandardReportsViewerTree.aspx">
          {t('components.reportsSection.standardReports', 'Standard Reports')}
        </AnchorListItem>
      )}
      {shouldShowLegacyReports && (
        <AnchorListItem inset href="/SSQV4/Reports/SubContractorDetailReport.aspx?edx=">
          {t('components.reportsSection.subcontractors', 'Subcontractors')}
        </AnchorListItem>
      )}
    </SectionContainer>
  );
};
